import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.tsx";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * The profile row at the foot of the sidebar opens a menu rather than acting
 * on its own. It looks like it leads to your account, and it used to sign you
 * out on the first click, discarding whatever you were halfway through.
 */
export function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Following the account link navigates; the menu should not linger over the page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on navigation by design
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="user-menu">
      {open && (
        <div className="user-pop" role="menu" aria-label="Account">
          <div className="user-pop-head">
            <div className="name">{user?.name}</div>
            <div className="email">{user?.email}</div>
            <span className={`pill ${user?.role === "admin" ? "info" : "mute"}`}>{user?.role}</span>
          </div>
          <Link to="/settings/account" role="menuitem" className="user-pop-item">
            Account &amp; password
          </Link>
          <button
            type="button"
            role="menuitem"
            className="user-pop-item"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      )}

      <button
        type="button"
        className={`user-row${open ? " open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <div className="avatar">{initials(user?.name ?? "?")}</div>
        <div className="user-info">
          <div className="name">{user?.name}</div>
          <div className="email">{user?.email}</div>
        </div>
        <span className="chev" aria-hidden="true">
          ▴
        </span>
      </button>
    </div>
  );
}
